import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Steps, Button, Space, Form, Spin, Empty, Descriptions, Alert, Result } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { showSuccessMessage, showErrorMessage } from '../../services/messages';
import DrumQuantities from '../../components/drum/DrumQuantities';
import { WizardHeader, QuantityStep, useIsOperator, FLOW_COLORS, primaryButtonStyle, BackToHome } from '../../components/drum/WizardParts';
import EDrumPurchaseStatus from '../../enums/EDrumPurchaseStatus';

/**
 * Wizard didático de conferência de compra do tambor (Operador-comprador / Admin).
 *
 * Layout adaptado por papel:
 *  - Operador (mobile/PWA): cabeçalho compacto + UM campo por passo.
 *  - Admin (desktop): <Steps> + <DrumQuantities> agrupado.
 *
 * Campos numéricos com inputMode="decimal" (teclado numérico no mobile).
 * Salva via api.drum.conference (registra usuário, data/hora e AuditLog).
 */
const ConferenceDrum = () => {
  const { idDrum } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isOperator = useIsOperator();
  const flowColor = location.state?.color || FLOW_COLORS.conference;

  const [form] = Form.useForm();
  const [drum, setDrum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => { loadDrum(); }, [idDrum]);

  const loadDrum = async () => {
    setLoading(true);
    try {
      const response = await api.drum.get(idDrum);
      const d = response.data;
      setDrum(d);
      // Defesa em profundidade: tambor já conferido → view só-leitura.
      if (d?.purchase_status === EDrumPurchaseStatus.CONFERRED.code) {
        navigate(`/drums/${idDrum}/view`, { replace: true });
        return;
      }
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || 'Erro ao carregar tambor.');
    } finally {
      setLoading(false);
    }
  };

  const values = Form.useWatch([], form);
  const total = (Number(values?.purchase_solvent) + Number(values?.purchase_borra) + Number(values?.purchase_water) + Number(values?.purchase_missing)) || 0;

  const qtyFilled = () => [values?.purchase_solvent, values?.purchase_borra, values?.purchase_water, values?.purchase_missing]
    .every(v => v != null && v !== '' && !Number.isNaN(Number(v)));

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const body = {
        purchase_solvent: values.purchase_solvent,
        purchase_borra: values.purchase_borra,
        purchase_water: values.purchase_water,
        purchase_missing: values.purchase_missing,
      };
      await api.drum.conference(idDrum, body);
      setDone(true);
      showSuccessMessage('Conferência de compra salva com sucesso!');
    } catch (error) {
      showErrorMessage(error?.response?.data?.error || 'Erro ao salvar conferência.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin />;
  if (!drum) return <Empty description="Tambor não encontrado." />;
  if (done) {
    return (
      <Card>
        <Result
          status="success"
          title="Conferência de compra concluída!"
          subTitle={`Tambor ${drum.code} conferido e registrado no histórico.`}
          extra={[
            <Button
              type="primary"
              key="entry"
              size={isOperator ? 'large' : 'middle'}
              onClick={() => navigate('/entry', { state: { flow: 'conference', color: flowColor } })}
              style={isOperator ? primaryButtonStyle(flowColor) : undefined}
            >
              Conferir outro tambor
            </Button>,
            isOperator ? (
              <Button key="home" size="large" onClick={() => navigate('/home')}>Voltar para a Home</Button>
            ) : (
              <Button key="list" onClick={() => navigate('/drums')}>Ver tambores</Button>
            )
          ]}
        />
      </Card>
    );
  }

  const alreadyConferred = drum.purchase_status === EDrumPurchaseStatus.CONFERRED.code;

  const qtySteps = isOperator
    ? [
        { title: 'Solvente', content: <QuantityStep form={form} name="purchase_solvent" label="Solvente" hint="Quantidade em litros." />, validate: ['purchase_solvent'], canNext: () => values?.purchase_solvent != null && values.purchase_solvent !== '' },
        { title: 'Borra', content: <QuantityStep form={form} name="purchase_borra" label="Borra" hint="Quantidade em litros." />, validate: ['purchase_borra'], canNext: () => values?.purchase_borra != null && values.purchase_borra !== '' },
        { title: 'Água', content: <QuantityStep form={form} name="purchase_water" label="Água" hint="Quantidade em litros." />, validate: ['purchase_water'], canNext: () => values?.purchase_water != null && values.purchase_water !== '' },
        { title: 'Faltando', content: <QuantityStep form={form} name="purchase_missing" label="Faltando" hint="Quantidade em litros." />, validate: ['purchase_missing'], canNext: () => values?.purchase_missing != null && values.purchase_missing !== '' },
      ]
    : [
        { title: 'Quantidades', content: <DrumQuantities form={form} prefix="purchase_" strict={false} />, validate: ['purchase_solvent', 'purchase_borra', 'purchase_water', 'purchase_missing'], canNext: qtyFilled },
      ];

  const steps = [
    {
      title: 'Tambor',
      content: (
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label="Código"><strong>{drum.code}</strong></Descriptions.Item>
          <Descriptions.Item label="Cliente">{drum.client?.company_name || drum.client?.nickname || '—'}</Descriptions.Item>
          <Descriptions.Item label="Lote">#{drum.batch_id}</Descriptions.Item>
          <Descriptions.Item label="Status conferência">{EDrumPurchaseStatus.getByCode(drum.purchase_status)?.description}</Descriptions.Item>
        </Descriptions>
      ),
    },
    ...qtySteps,
    {
      title: 'Confirmar',
      content: (
        <div>
          {alreadyConferred && (
            <Alert
              style={{ marginBottom: 16 }}
              type="warning"
              showIcon
              message="Este tambor já foi conferido. A nova conferência será registrada no histórico."
            />
          )}
          {total !== 200 && (
            <Alert
              style={{ marginBottom: 16 }}
              type="warning"
              showIcon
              message={total > 200
                ? `Atenção: o total informado (${total.toFixed(2).replace('.', ',')} L) passou ${(total - 200).toFixed(2).replace('.', ',')} L dos 200 L. Confirme se os valores estão corretos.`
                : `Atenção: o total informado (${total.toFixed(2).replace('.', ',')} L) ficou ${(200 - total).toFixed(2).replace('.', ',')} L abaixo dos 200 L. Confirme se os valores estão corretos.`}
            />
          )}
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Solvente">{values?.purchase_solvent} L</Descriptions.Item>
            <Descriptions.Item label="Borra">{values?.purchase_borra} L</Descriptions.Item>
            <Descriptions.Item label="Água">{values?.purchase_water} L</Descriptions.Item>
            <Descriptions.Item label="Faltando">{values?.purchase_missing} L</Descriptions.Item>
            <Descriptions.Item label="Total">{total.toFixed(2).replace('.', ',')} L</Descriptions.Item>
          </Descriptions>
          <Alert style={{ marginTop: 16 }} type="info" showIcon message="Confira os dados acima. Ao confirmar, a conferência será salva com seu usuário e a data/hora atuais." />
        </div>
      ),
    },
  ];

  const step = steps[current];
  const canNext = () => (typeof step.canNext === 'function' ? step.canNext() : true);

  const next = async () => {
    if (step.validate) {
      try { await form.validateFields(step.validate); }
      catch { return; }
    }
    setCurrent(c => Math.min(c + 1, steps.length - 1));
  };

  const prev = () => setCurrent(c => Math.max(c - 1, 0));

  return (
    <div style={{ maxWidth: isOperator ? 720 : 760, margin: '0 auto' }}>
      {isOperator && <BackToHome color={flowColor} />}
      <Card>
        {isOperator ? (
          <WizardHeader drumCode={drum.code} current={current} total={steps.length} title={step.title} color={flowColor} />
        ) : (
          <>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Conferência de compra — {drum.code}</div>
            <Steps
              current={current}
              size="small"
              items={steps.map(s => ({ title: s.title }))}
              style={{ marginBottom: 24 }}
            />
          </>
        )}
        <Form form={form} layout="vertical" preserve>
          {/* Mantém todas as etapas montadas (apenas escondidas) para que os
              valores dos Form.Item persistam e o Form.useWatch([]) enxergue os
              campos na etapa de confirmação. */}
          {steps.map((s, idx) => (
            <div key={idx} style={{ display: idx === current ? 'block' : 'none' }}>
              {s.content}
            </div>
          ))}
        </Form>
        <Space style={{ marginTop: 24, width: '100%', justifyContent: 'space-between' }} wrap>
          {current > 0 ? (
            <Button size={isOperator ? 'large' : 'middle'} onClick={prev}>Voltar</Button>
          ) : <span />}
          {current < steps.length - 1 && (
            <Button
              type="primary"
              size={isOperator ? 'large' : 'middle'}
              disabled={!canNext()}
              onClick={next}
              style={isOperator ? { minWidth: 160, ...primaryButtonStyle(flowColor) } : undefined}
            >
              Avançar
            </Button>
          )}
          {current === steps.length - 1 && (
            <Button
              type="primary"
              size={isOperator ? 'large' : 'middle'}
              icon={<CheckCircleOutlined />}
              loading={saving}
              onClick={handleConfirm}
              style={isOperator ? { minWidth: 200, ...primaryButtonStyle(flowColor) } : undefined}
            >
              Confirmar e salvar
            </Button>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default ConferenceDrum;
